/**
 * How much of a position a take-profit / stop-loss leg may cover.
 *
 * The same rule as a manual close — a trigger leg can cover the whole position
 * and nothing more — so this is `closePositionMax` under the TP/SL modal's own
 * name rather than a second implementation that could drift from it.
 *
 * The one thing that is genuinely different is the leg's sentinel: a leg with no
 * explicit size closes the WHOLE position (`CLOSE_FULL_POSITION`). That makes
 * "0" catastrophic rather than merely wrong, which is why `resolveCloseSize`
 * threw rather than returning it. Here the three outcomes are separate values,
 * so the caller can tell "close it all" from "this size floors to nothing"
 * without a throw.
 */
import { Dec, dec } from "../decimal";
import { MarketSteps } from "../spec";
import {
  ClosePositionMaxInput,
  closePositionMax,
  closeSizeForPercent,
  resolveCloseSize,
} from "./closePositionMax";
import { MaxResult } from "./types";

export type TpslSizeMaxInput = ClosePositionMaxInput;

/** The position itself: exact, unfloored, never capped by the market minimum. */
export const tpslSizeMax = (input: TpslSizeMaxInput): MaxResult =>
  closePositionMax(input);

/** The 25/50/75/100% chips. 100% is the position exactly; "0" is "nothing here". */
export const tpslSizeForPercent = (
  positionAmount: Dec | number | null | undefined,
  pct: number,
  steps: MarketSteps
): Dec => closeSizeForPercent(positionAmount, pct, steps);

export type TpslCloseSize =
  /** No explicit size: the leg closes the whole position. */
  | { kind: "full" }
  /** An explicit, step-aligned size to write into the leg. */
  | { kind: "size"; size: Dec }
  /** A size was asked for, but it floors to nothing — do not send the leg. */
  | { kind: "below_step" };

/**
 * What a leg's size field resolves to.
 *
 * Absent, empty or non-positive reads as "close it all", exactly as the leg
 * builder treats an omitted size. A size at or above the position is the
 * position's own amount; anything else floors onto the step. A positive size
 * that floors away is `below_step`, never `full` — silently promoting it to a
 * full close is the failure this type exists to prevent.
 */
export const resolveTpslCloseSize = (
  requested: Dec | number | null | undefined,
  positionAmount: Dec | number | null | undefined,
  steps: MarketSteps
): TpslCloseSize => {
  const resolved = resolveCloseSize(requested, positionAmount, steps);
  if (resolved !== null) return { kind: "size", size: resolved };
  // `resolveCloseSize` returns null both for "nothing was asked for" and for
  // "what was asked for floors away"; only the first may become a full close.
  const asked = dec.parse(requested ?? null);
  return asked !== null && dec.gt(asked, "0")
    ? { kind: "below_step" }
    : { kind: "full" };
};
